import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Search, X, ChevronDown, Check } from 'lucide-react';

export default function SearchableSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Selecione ou digite para buscar...',
  getOptionLabel,
  getOptionValue = (opt) => opt.id,
  renderOption,
  className = '',
  disabled = false,
  usePortal = false,
  autoOpen = false,
  height = '38px',
  fontSize = '0.85rem'
}) {
  const [isOpen, setIsOpen] = useState(autoOpen || false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const selectedOption = options.find(opt => String(getOptionValue(opt)) === String(value));

  // Reset search term when dropdown state changes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (autoOpen) {
      setIsOpen(true);
    }
  }, [autoOpen]);

  const updateDropdownPos = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const opensUp = spaceBelow < 250 && rect.top > 250;
      setDropdownPos({
        top: opensUp ? Math.max(10, rect.top - 255) : rect.bottom + 4,
        left: Math.max(10, Math.min(rect.left, window.innerWidth - 380)),
        width: Math.max(rect.width, 360)
      });
    }
  };

  useEffect(() => {
    if (isOpen && usePortal) {
      updateDropdownPos();
      window.addEventListener('resize', updateDropdownPos);
      window.addEventListener('scroll', updateDropdownPos, true);
    }
    return () => {
      window.removeEventListener('resize', updateDropdownPos);
      window.removeEventListener('scroll', updateDropdownPos, true);
    };
  }, [isOpen, usePortal]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const insideContainer = containerRef.current && containerRef.current.contains(event.target);
      const insideDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      if (!insideContainer && !insideDropdown) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const label = getOptionLabel ? getOptionLabel(opt) : (opt.label || opt.name || String(getOptionValue(opt)));
    return String(label).toLowerCase().includes(term);
  });

  // Limit visible items to 50 for instantaneous rendering performance
  const visibleOptions = filteredOptions.slice(0, 50);

  const handleSelect = (opt) => {
    onChange(getOptionValue(opt));
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Enter' && isOpen && visibleOptions.length > 0) {
      e.preventDefault();
      handleSelect(visibleOptions[0]);
    }
  };

  const dropdownContent = (
    <>
      {filteredOptions.length === 0 ? (
        <div style={{ padding: '0.75rem', fontSize: '0.85rem', color: '#64748b', textAlign: 'center' }}>
          Nenhum item encontrado com "{searchTerm}"
        </div>
      ) : (
        <>
          {filteredOptions.length > 50 && (
            <div style={{ 
              padding: '0.35rem 0.5rem', 
              fontSize: '0.75rem', 
              color: '#64748b', 
              backgroundColor: '#f8fafc',
              borderRadius: '4px',
              marginBottom: '0.35rem',
              fontWeight: '500' 
            }}>
              Exibindo 50 de {filteredOptions.length} resultados. Digite para refinar...
            </div>
          )}
          {visibleOptions.map((opt) => {
            const optVal = getOptionValue(opt);
            const isSelected = String(optVal) === String(value);

            return (
              <div
                key={optVal}
                onClick={() => handleSelect(opt)}
                style={{
                  padding: '0.5rem 0.65rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                  color: isSelected ? '#1d4ed8' : '#334155',
                  transition: 'background-color 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = '#f1f5f9';
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div style={{ flexGrow: 1, minWidth: 0, paddingRight: '0.5rem' }}>
                  {renderOption ? renderOption(opt) : (getOptionLabel ? getOptionLabel(opt) : opt.label)}
                </div>
                {isSelected && <Check size={14} style={{ color: '#2563eb', flexShrink: 0 }} />}
              </div>
            );
          })}
        </>
      )}
    </>
  );

  return (
    <div 
      ref={containerRef} 
      className={`searchable-select-container ${className}`} 
      style={{ position: 'relative', width: '100%' }}
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          border: isOpen ? '1px solid var(--primary-color, #8b5cf6)' : '1px solid var(--border-color, #cbd5e1)',
          borderRadius: 'var(--radius-md, 6px)',
          backgroundColor: disabled ? '#f8fafc' : '#ffffff',
          padding: '0 0.5rem',
          height: height || '38px',
          boxSizing: 'border-box',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease-in-out',
          boxShadow: isOpen ? '0 0 0 3px rgba(139, 92, 246, 0.15)' : 'none'
        }}
        onClick={() => {
          if (!disabled) setIsOpen(!isOpen);
        }}
      >
        <Search size={14} style={{ color: '#94a3b8', marginRight: '0.35rem', flexShrink: 0 }} />

        {isOpen ? (
          <input
            type="text"
            autoFocus
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={selectedOption ? (getOptionLabel ? getOptionLabel(selectedOption) : selectedOption.label) : placeholder}
            style={{
              border: 'none',
              outline: 'none',
              width: '100%',
              fontSize: fontSize || '0.85rem',
              color: 'var(--text-main, #1e293b)',
              backgroundColor: 'transparent',
              padding: 0,
              margin: 0
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span style={{
            flexGrow: 1,
            fontSize: fontSize || '0.85rem',
            color: selectedOption ? 'var(--text-main, #1e293b)' : '#94a3b8',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: selectedOption ? '500' : '400'
          }}>
            {selectedOption 
              ? (getOptionLabel ? getOptionLabel(selectedOption) : selectedOption.label)
              : placeholder
            }
          </span>
        )}

        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              background: 'none',
              border: 'none',
              padding: '2px',
              cursor: 'pointer',
              color: '#94a3b8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              marginLeft: '0.25rem'
            }}
            title="Limpar seleção"
          >
            <X size={14} />
          </button>
        )}

        <ChevronDown 
          size={14} 
          style={{ 
            color: '#64748b', 
            marginLeft: '0.25rem', 
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            flexShrink: 0
          }} 
        />
      </div>

      {isOpen && (
        usePortal ? (
          ReactDOM.createPortal(
            <div
              ref={dropdownRef}
              style={{
                position: 'fixed',
                top: dropdownPos.top,
                left: dropdownPos.left,
                width: dropdownPos.width,
                maxWidth: '92vw',
                backgroundColor: '#ffffff',
                border: '1px solid var(--border-color, #e2e8f0)',
                borderRadius: 'var(--radius-md, 8px)',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.25), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
                maxHeight: '260px',
                overflowY: 'auto',
                zIndex: 99999,
                padding: '0.35rem',
                boxSizing: 'border-box'
              }}
            >
              {dropdownContent}
            </div>,
            document.body
          )
        ) : (
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              backgroundColor: '#ffffff',
              border: '1px solid var(--border-color, #e2e8f0)',
              borderRadius: 'var(--radius-md, 8px)',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
              maxHeight: '260px',
              overflowY: 'auto',
              zIndex: 1100,
              padding: '0.35rem'
            }}
          >
            {dropdownContent}
          </div>
        )
      )}
    </div>
  );
}
